import { useEffect } from 'react';
import { useQueryStates } from 'nuqs';
import { flowPanelsParams } from '../../../../lib/urlParams';
import { useFlowPanelsStore, openPanel, closePanel, setFlowClosed } from './flow-panels-store';

/**
 * useFlowPanelsUrlSync — two-way nuqs transport between the V2 Flow shell's
 * panel store (`flow-panels-store.ts`) and `?panels=` / `?flowClosed=`
 * (`flowPanelsParams`, `lib/urlParams.ts`): present URL params seed the
 * store; the store then mirrors its full open-set back to the URL. Plan
 * `2026-09-30-005-v2-panel-container-plan.md`, U5/R11, D9.
 *
 * Pattern credit: `EnrollQueue/useDockUrlSync.ts`, the canonical paired
 * WRITE/READ effects in this codebase. Its loop-safety rule is what
 * keeps this pair from oscillating, and it is repeated here verbatim in
 * intent: **the READ effect's dependency array lists ONLY the URL's own
 * params, never the local state it mirrors.** Watching the local state would
 * make "the rep just closed a panel, so the store and the still-stale URL
 * disagree" indistinguishable from "a pasted deep link just arrived", and
 * READ would re-open the panel the rep just closed — the exact regression
 * that file documents. WRITE is the only effect allowed to react to local
 * state; it corrects the URL, and READ then sees a match and no-ops.
 *
 * Same-commit ordering (D9): READ is declared FIRST and WRITE second, because
 * React runs a component's effects in declaration order — on a deep-linked
 * mount both run in the SAME commit, and READ must seed the store before the
 * mirror runs. That ordering is only sufficient because **both effect bodies
 * read the store through `useFlowPanelsStore.getState()`, never through the
 * render's selector values**: WRITE's closure still holds the pre-READ render
 * values (`chatOpen: false`), so a closure-reading WRITE would compute
 * "nothing open" and immediately clear the very params READ just consumed.
 * The three selector subscriptions below exist ONLY so the effects re-run
 * when the store changes; the truth inside a body is always `getState()`.
 *
 * `history: 'replace'`, matching every other transport group in this app
 * (`flowChatParams`, `flowRequestParams`) — panel
 * toggles are not back-button waypoints.
 */
export function useFlowPanelsUrlSync(): void {
  const [{ panels, flowClosed: urlFlowClosed }, setParams] = useQueryStates(flowPanelsParams, {
    history: 'replace',
  });

  // A string, not the parsed array: `useQueryStates` hands back a fresh array
  // identity on every render, so depending on `panels` directly would re-fire
  // the READ effect forever (D9).
  const panelsKey = panels?.join(',') ?? null;

  // Subscriptions only — see the "truth inside a body is always `getState()`"
  // note above. These exist so the WRITE effect re-runs on a store change.
  const chatOpen = useFlowPanelsStore((s) => s.chatOpen);
  const actionsOpen = useFlowPanelsStore((s) => s.actionsOpen);
  const flowClosed = useFlowPanelsStore((s) => s.flowClosed);

  // READ direction — URL -> store (deep link / paste / refresh).
  //
  // D9: only a PRESENT param acts. An absent `panels` is a no-op, never
  // "close everything" — the drill-down `navigate('?tab=')` collision
  // documented at `lib/urlParams.ts` (the `flowRequestParams` paragraph,
  // ~lines 76-82) strips nuqs-owned params from the URL without the rep
  // touching a panel, and treating that as a close intent would silently slam
  // both side panels shut mid-navigation. A stripped param is `null`, never
  // `false`, so this rule does not extend to `flowClosed`: `flowClosed` is
  // applied whenever it is PRESENT — `true` or `false`. WRITE only ever
  // serializes it as `true` or absent, so a `false` on the wire is always a
  // hand-typed deep link, and the deep link wins; WRITE then normalizes it
  // back to absent.
  useEffect(() => {
    if (panels !== null) {
      const state = useFlowPanelsStore.getState();
      const wantChat = panels.includes('chat');
      const wantActions = panels.includes('actions');
      if (wantChat !== state.chatOpen) {
        if (wantChat) openPanel('chat');
        else closePanel('chat');
      }
      if (wantActions !== state.actionsOpen) {
        if (wantActions) openPanel('actions');
        else closePanel('actions');
      }
    }
    if (urlFlowClosed !== null && urlFlowClosed !== useFlowPanelsStore.getState().flowClosed) {
      setFlowClosed(urlFlowClosed);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- deliberately NOT reactive to the store (the loop-safety rule above), and `panels` is a fresh array identity every render — `panelsKey` is its stable string form (D9).
  }, [panelsKey, urlFlowClosed]);

  // WRITE direction — store -> URL. Mirrors the FULL open-set every time, so
  // the URL is always the complete picture rather than a patch history.
  useEffect(() => {
    const s = useFlowPanelsStore.getState();
    const wantPanels = [...(s.chatOpen ? (['chat'] as const) : []), ...(s.actionsOpen ? (['actions'] as const) : [])];
    const wantPanelsParam = wantPanels.length ? [...wantPanels] : null;
    const wantClosed = s.flowClosed ? true : null;
    // Pure value comparison, no "did I just write it" ref — matches or it
    // doesn't, regardless of which direction most recently changed something.
    if ((wantPanelsParam?.join(',') ?? null) === panelsKey && wantClosed === urlFlowClosed) return;
    void setParams({ panels: wantPanelsParam, flowClosed: wantClosed });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- `chatOpen`/`actionsOpen`/`flowClosed` are intentionally deps-only (they re-run this effect on a store change) while the body reads `getState()`; see the same-commit ordering note above.
  }, [chatOpen, actionsOpen, flowClosed, panelsKey, urlFlowClosed, setParams]);
}
